import { type PrimitiveAtom, useAtom, useAtomValue } from 'jotai'
import React from 'react'

import { brandGroupIdAtom } from '~/atoms'
import {
  type Brand,
  useSurveyQueryAvailableBrandGroups,
} from '~/hooks/useSurveyQuery'

import {
  BrandTreeViewBoxInner,
  type BrandTreeViewBoxInnerProps,
} from './BrandTreeViewBoxInner'

export type BrandTreeViewBoxProps = Pick<
  BrandTreeViewBoxInnerProps<Brand>,
  'onSelectedBrandIdChange'
> & {
  brandIdsAtom: PrimitiveAtom<Brand['id'][]>
  'data-testid'?: string
}

export const BrandTreeViewBox = ({
  brandIdsAtom,
  'data-testid': dataTestId = 'components-features-brand-tree-view-box',
  onSelectedBrandIdChange,
}: BrandTreeViewBoxProps) => {
  const brandGroups = useSurveyQueryAvailableBrandGroups()
  const brandGroupId = useAtomValue(brandGroupIdAtom)
  const brands = React.useMemo(
    () => brandGroups.find(({ id }) => id === brandGroupId)?.brands ?? [],
    [brandGroupId, brandGroups],
  )

  const [brandIds, setBrandIds] = useAtom(brandIdsAtom)

  const handleSelectedBrandIdChange = React.useCallback<
    EH<BrandTreeViewBoxInnerProps<Brand>, 'onSelectedBrandIdChange'>
  >(
    (brandIds) => {
      setBrandIds(brandIds)

      onSelectedBrandIdChange?.(brandIds)
    },
    [onSelectedBrandIdChange, setBrandIds],
  )

  return (
    <BrandTreeViewBoxInner
      brands={brands}
      data-testid={dataTestId}
      selectedBrandIds={brandIds}
      onSelectedBrandIdChange={handleSelectedBrandIdChange}
    />
  )
}
